import type { FloatingRect } from './MemoWindow';
import { getActiveAccountId } from '@auw/api-client';
import { activeTenantKey as tenantKey } from '@auw/tenant';

export interface PersistedMemoData {
  id: string;
  content: string;
}

export interface PersistedMemoWindow {
  id: string;
  type: 'memo';
  rect: FloatingRect;
  pinned: boolean;
  opacity: number;
  zIndex: number;
  memo: PersistedMemoData;
}

interface PersistedCanvasPayload {
  updatedAt: number;
  windows: PersistedMemoWindow[];
}

export interface PersistedWidgetSnapshot {
  updatedAt: number;
  windows: PersistedMemoWindow[];
}

/* Resolved lazily so this module body can evaluate before the host
 * has registered its tenant manifest. Vite/Rollup chunk-split may
 * co-locate this module with a chunk evaluated alongside the entry;
 * a top-level `tenantKey(...)` would throw under that condition. */
let _storageKeyPrefix: string | null = null;
function getStorageKeyPrefix(): string {
  if (_storageKeyPrefix === null) _storageKeyPrefix = tenantKey('widget_canvas');
  return _storageKeyPrefix;
}

let _dbName: string | null = null;
function getDbName(): string {
  if (_dbName === null) _dbName = tenantKey('widget_assets');
  return _dbName;
}

const IMG_TOKEN_RE = /memo-img:([a-zA-Z0-9_]+)/g;

const DB_VERSION = 1;
const IMG_STORE = 'memo_images';

interface MemoImageRow {
  id: string;
  dataUrl: string;
  updatedAt: number;
}

function getWidgetStorageKey(): string {
  const accountId = getActiveAccountId();
  return `${getStorageKeyPrefix()}:${accountId || 'anonymous'}`;
}

function getScopedMemoImageKey(imageId: string): string {
  const accountId = getActiveAccountId();
  return `${accountId || 'anonymous'}:${imageId}`;
}

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function canUseIndexedDb(): boolean {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function isFiniteNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function toRect(raw: unknown): FloatingRect | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (!isFiniteNum(obj.x) || !isFiniteNum(obj.y) || !isFiniteNum(obj.w) || !isFiniteNum(obj.h)) {
    return null;
  }
  return {
    x: obj.x,
    y: obj.y,
    w: obj.w,
    h: obj.h,
  };
}

function normalizeWindow(raw: unknown): PersistedMemoWindow | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (obj.type !== 'memo') return null;
  if (typeof obj.id !== 'string' || !obj.id) return null;
  if (typeof obj.pinned !== 'boolean') return null;
  if (!isFiniteNum(obj.zIndex)) return null;
  const opacityRaw = isFiniteNum(obj.opacity) ? obj.opacity : 1;
  const opacity = Math.min(1, Math.max(0.35, opacityRaw));

  const rect = toRect(obj.rect);
  if (!rect) return null;

  const memo = obj.memo as Record<string, unknown> | undefined;
  if (!memo || typeof memo.id !== 'string' || typeof memo.content !== 'string') return null;

  return {
    id: obj.id,
    type: 'memo',
    rect,
    pinned: obj.pinned,
    opacity,
    zIndex: obj.zIndex,
    memo: {
      id: memo.id,
      content: memo.content,
    },
  };
}

export function loadPersistedWidgetSnapshot(): PersistedWidgetSnapshot {
  if (!canUseLocalStorage()) return { updatedAt: 0, windows: [] };
  try {
    const raw = window.localStorage.getItem(getWidgetStorageKey());
    if (!raw) return { updatedAt: 0, windows: [] };
    const parsed = JSON.parse(raw) as Partial<PersistedCanvasPayload>;
    if (!parsed || !Array.isArray(parsed.windows)) {
      return { updatedAt: 0, windows: [] };
    }
    const out: PersistedMemoWindow[] = [];
    for (const item of parsed.windows) {
      const normalized = normalizeWindow(item);
      if (normalized) out.push(normalized);
    }
    return {
      updatedAt: isFiniteNum(parsed.updatedAt) ? parsed.updatedAt : 0,
      windows: out,
    };
  } catch {
    return { updatedAt: 0, windows: [] };
  }
}

export function loadPersistedWidgetWindows(): PersistedMemoWindow[] {
  return loadPersistedWidgetSnapshot().windows;
}

export function savePersistedWidgetWindows(
  windows: PersistedMemoWindow[],
  updatedAt: number = Date.now(),
): void {
  if (!canUseLocalStorage()) return;
  try {
    const payload: PersistedCanvasPayload = {
      updatedAt,
      windows,
    };
    window.localStorage.setItem(getWidgetStorageKey(), JSON.stringify(payload));
  } catch {
    // storage quota/full 상태에서는 메모리 상태만 유지
  }
}

export function extractMemoImageIds(content: string): string[] {
  const ids = new Set<string>();
  IMG_TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null = IMG_TOKEN_RE.exec(content);
  while (match) {
    const id = match[1];
    if (id) ids.add(id);
    match = IMG_TOKEN_RE.exec(content);
  }
  return [...ids];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(getDbName(), DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IMG_STORE)) {
        db.createObjectStore(IMG_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  try {
    const tx = db.transaction(IMG_STORE, mode);
    const store = tx.objectStore(IMG_STORE);
    const result = await work(store);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return result;
  } finally {
    db.close();
  }
}

export async function upsertMemoImageAsset(id: string, dataUrl: string): Promise<void> {
  if (!canUseIndexedDb() || !id || !dataUrl) return;
  try {
    await withStore('readwrite', async (store) => {
      await new Promise<void>((resolve, reject) => {
        const req = store.put({
          id: getScopedMemoImageKey(id),
          dataUrl,
          updatedAt: Date.now(),
        } as MemoImageRow);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    });
  } catch {
    // 실패해도 메모리는 유지
  }
}

export async function loadMemoImageAssets(ids: string[]): Promise<Record<string, string>> {
  if (!canUseIndexedDb() || ids.length === 0) return {};
  try {
    const unique = [...new Set(ids)];
    return await withStore('readonly', async (store) => {
      const out: Record<string, string> = {};
      for (const id of unique) {
        // eslint-disable-next-line no-await-in-loop
        const row = await new Promise<MemoImageRow | undefined>((resolve, reject) => {
          const req = store.get(getScopedMemoImageKey(id));
          req.onsuccess = () => resolve(req.result as MemoImageRow | undefined);
          req.onerror = () => reject(req.error);
        });
        if (row?.dataUrl) out[id] = row.dataUrl;
      }
      return out;
    });
  } catch {
    return {};
  }
}

export async function deleteMemoImageAssets(ids: string[]): Promise<void> {
  if (!canUseIndexedDb() || ids.length === 0) return;
  try {
    const unique = [...new Set(ids)];
    await withStore('readwrite', async (store) => {
      for (const id of unique) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve, reject) => {
          const req = store.delete(getScopedMemoImageKey(id));
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
      }
    });
  } catch {
    // Ignore cleanup failure
  }
}

export async function pruneOrphanMemoImageAssets(usedIds: Set<string>): Promise<void> {
  if (!canUseIndexedDb()) return;
  try {
    await withStore('readwrite', async (store) => {
      const keyPrefix = `${getActiveAccountId() || 'anonymous'}:`;
      const keys = await new Promise<IDBValidKey[]>((resolve, reject) => {
        const req = store.getAllKeys();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });

      for (const key of keys) {
        const scopedId = String(key);
        if (!scopedId.startsWith(keyPrefix)) continue;
        const id = scopedId.slice(keyPrefix.length);
        if (usedIds.has(id)) continue;
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve, reject) => {
          const req = store.delete(scopedId);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
      }
    });
  } catch {
    // Ignore cleanup failure
  }
}
